import { useInvestments } from '../queries';
import { useTranslation } from 'react-i18next';
import {
  getAfterTaxSavingsInvestments,
  getGridSumTableColumnsForInvestments,
  getInvalidateInvestmentProactivelyOptions,
  getTotalRecurringContributionsForInvestments,
} from '../util';
import { RecurringFrequency } from '../../generated/graphql';
import { useDisplayRecurringMonetaryAmount } from '../../DataPoint';
import { recurringMonetaryAmountConverter } from '../../util';
import { useQueryClient } from '@tanstack/react-query';
import { investmentKeys } from '../queryKeys';
import { InvestmentSumTableArgs } from '../types';

export interface UseAfterTaxSavingsContributionsSumTableArgs extends Pick<InvestmentSumTableArgs, 'onClickItem'> {
  householdID: string | undefined | null;
}

export function useAfterTaxSavingsContributionsSumTable({
  householdID,
  onClickItem,
}: UseAfterTaxSavingsContributionsSumTableArgs) {
  const { displayRecurringMonetaryAmount } = useDisplayRecurringMonetaryAmount();
  const { t: tInvestment } = useTranslation('investment');
  const { t: tUI } = useTranslation('UI');
  const { investments, investmentsQuery } = useInvestments({ householdID });

  const isLoading = investmentsQuery.isLoading;
  const afterTaxSavings = getAfterTaxSavingsInvestments(investments);
  const annualContributions = getTotalRecurringContributionsForInvestments(afterTaxSavings, {
    frequency: RecurringFrequency.Annually,
  });
  const monthlyContributionsDisplay = displayRecurringMonetaryAmount(
    recurringMonetaryAmountConverter(annualContributions, RecurringFrequency.Monthly)
  );
  const queryClient = useQueryClient();
  const columns = getGridSumTableColumnsForInvestments({
    tInvestment,
    onClickItem,
    tUI,
    afterUpdate: async (investment) => {
      await queryClient.invalidateQueries(investmentKeys.investments(householdID));
      await queryClient.invalidateQueries(getInvalidateInvestmentProactivelyOptions(investment));
    },
  });

  return {
    isLoading,
    tInvestment,
    annualContributions,
    afterTaxSavings,
    monthlyContributionsDisplay,
    columns,
  };
}
